import { useState } from "react";
import { AcademicCapIcon, GlobeAltIcon, MagnifyingGlassIcon } from "@heroicons/react/24/outline";
import logo from "../assets/img/hec-logo.png";
import buildingPhoto from "../assets/img/mineduc-house.jpg";
import { loginWith } from "../auth/keycloak";
import { rememberReturnPath } from "../auth/returnPath";
import { Button, Loading } from "./ui";
import { destinationOf, placeOf, formatDuration, formatKigaliDate, offeredToStudents, usePublicScholarships } from "./scholarships/model";
import { abroadCallPath, useOpenAbroadCalls } from "./abroadLoans/student";
import { useAutoScroll } from "../hooks/useAutoScroll";
import CallListItem, { toggleIn } from "./calls/CallListItem";

/**
 * The page everyone lands on before signing in: the two Keycloak entry points, and the
 * scholarships that are currently open.
 *
 * <p>Replaces the old SignIn page. Signing in, registering and resetting a password are all
 * Keycloak's job now, so none of those forms live here.
 */
// After signing in, the student lands on the page they chose, to apply
const signInToApply = (path) => {
  rememberReturnPath(path);
  loginWith("student");
};

// An abroad loan call as the list shows a scholarship: the student studies where they were admitted
const listingOf = (call) => ({
  id: call.id,
  name: call.name,
  place: "Abroad loan · where you are admitted",
  publicSummary: call.publicSummary,
  description: call.description,
  closesAt: call.closesAt,
  programmes: call.programmes.map(({ programme }) => programme),
  path: abroadCallPath(call.id),
});

// One open scholarship or loan call; expanding it shows the programmes a student chooses from
const ScholarshipItem = ({ scholarship, expanded, onToggle }) => (
  <CallListItem
    id={scholarship.id}
    name={scholarship.name}
    place={scholarship.place}
    closing={`Closes ${formatKigaliDate(scholarship.closesAt)}`}
    summary={scholarship.publicSummary || scholarship.description}
    programmes={(scholarship.programmes ?? []).map((p) => ({
      key: `${p.name}-${p.institutionName}`,
      name: p.name,
      meta: [placeOf(p), p.levelName, formatDuration(p.durationMonths)].filter(Boolean).join(" · "),
    }))}
    expanded={expanded}
    onToggle={() => onToggle(scholarship.id)}
    action={
      <Button size="sm" onClick={() => signInToApply(scholarship.path)}>
        Sign in to apply
      </Button>
    }
  />
);

const Landing = () => {
  // Published, listed scholarships open today (GET /public/scholarships); no sign-in needed
  const { data = [], isPending: scholarshipsLoading, isError } = usePublicScholarships();
  // Open abroad loan calls (GET /hec/abroad-loans/public/calls), listed with them as on dev-p
  const { data: loanCalls = [], isPending: loansLoading } = useOpenAbroadCalls({ signedIn: false });
  const loading = scholarshipsLoading || loansLoading;
  const open = [
    ...data.filter((s) => offeredToStudents(s.type)).map((s) => ({ ...s, place: [destinationOf(s), s.sponsor?.name].filter(Boolean).join(" · "), path: `/scholarships/${s.id}` })),
    ...loanCalls.map(listingOf),
  ].sort((a, b) => new Date(a.closesAt) - new Date(b.closesAt));
  // Shared by both copies of the looping list, so they stay the same height
  const [expanded, setExpanded] = useState(() => new Set());
  const toggle = toggleIn(setExpanded);
  const cards = open.map((scholarship) => <ScholarshipItem key={scholarship.id} scholarship={scholarship} expanded={expanded.has(scholarship.id)} onToggle={toggle} />);
  // A long list moves up slowly by itself; it stops while the reader points at or touches it
  const { containerRef, copyRef, nextRef, looping } = useAutoScroll(loading ? 0 : open.length);

  return (
    <div className="grid min-h-screen grid-cols-1 bg-slate-50 lg:h-screen lg:grid-cols-2 lg:overflow-hidden">
      {/* Sign-in panel */}
      <div className="flex min-w-0 items-center justify-center px-4 py-10 md:px-6">
        <div className="flex w-full max-w-md flex-col gap-4 rounded-xl bg-white p-6 shadow-sm md:p-8">
          <div className="flex flex-col items-center text-center">
            <img src={logo} alt="Higher Education Council" className="h-20 w-auto" />
            <h1 className="mt-4 text-2xl font-bold text-slate-900">Sign In</h1>
            <p className="mt-1 text-slate-500">Students and HEC staff sign in through the HEC account service.</p>
          </div>

          <div className="mt-2 flex flex-col gap-3">
            <Button fullWidth onClick={() => loginWith("student")}>
              Student login
            </Button>
            <Button fullWidth variant="outline" onClick={() => loginWith("staff")}>
              HEC staff login
            </Button>
          </div>

          <p className="text-center text-sm text-slate-500">
            New student? Choose <span className="font-medium text-brand-accent">Student login</span> and register on the next page.
          </p>
        </div>
      </div>

      {/* Open scholarships, over the brand photo */}
      <div
        className="flex min-h-0 min-w-0 flex-col bg-cover bg-center px-4 pt-10 md:px-8"
        style={{
          backgroundImage: `linear-gradient(rgba(34, 97, 129, 0.85), rgba(34, 97, 129, 0.92)), url(${buildingPhoto})`,
        }}
      >
        <div className="text-center text-white">
          <div className="flex items-center justify-center gap-2">
            <AcademicCapIcon className="h-7 w-7" />
            <h2 className="text-2xl font-bold md:text-3xl">Available Scholarships</h2>
          </div>
          <p className="mt-1 flex items-center justify-center gap-1 text-sm text-white/80">
            <GlobeAltIcon className="h-4 w-4" />
            Discover the scholarship opportunities
          </p>
          <span className="mt-4 inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1 text-xs font-medium ring-1 ring-white/20">
            <span className="h-2 w-2 rounded-full bg-brand-accent" />
            {open.length} open {open.length === 1 ? "opportunity" : "opportunities"} · Sign in for more information
          </span>
        </div>

        <div
          ref={containerRef}
          className="thin-scrollbar mx-auto mt-8 max-h-[75vh] w-full max-w-2xl overflow-y-auto lg:max-h-none lg:min-h-0 lg:flex-1"
        >
          {loading ? (
            <div className="rounded-lg bg-white">
              <Loading label="Loading scholarships…" />
            </div>
          ) : isError ? (
            <div className="rounded-lg bg-white py-16 text-center">
              <MagnifyingGlassIcon className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-900">Scholarships are unavailable</h3>
              <p className="mt-2 text-slate-500">We could not load the scholarships right now. Please try again later.</p>
            </div>
          ) : open.length > 0 ? (
            <>
              <div ref={copyRef} className="flex flex-col gap-4">
                {cards}
              </div>
              {/* The same cards again, so the scrolling list loops without a gap; hidden from screen readers and the keyboard */}
              {looping && (
                <div ref={nextRef} aria-hidden="true" inert className="mt-4 flex flex-col gap-4">
                  {cards}
                </div>
              )}
            </>
          ) : (
            <div className="rounded-lg bg-white py-16 text-center">
              <MagnifyingGlassIcon className="mx-auto mb-4 h-12 w-12 text-slate-300" />
              <h3 className="text-xl font-bold text-slate-900">No Open Scholarships</h3>
              <p className="mt-2 text-slate-500">There are no currently open scholarships. Check back later for new opportunities!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Landing;
